import { REGIONAL_DEFAULTS } from '../contract/regional';
import { resolvePreferences } from '../contract/resolve';
import type { ResolvedPreferences } from '../contract/types';
import { buildFormatBundle, type FormatBundle } from './bundle';
import { buildCalendarBundle, type CalendarBundle } from './calendar';
import { buildPhoneBundle, type PhoneBundle } from './phone';

/**
 * The bundle the core hands out while the rollout flag is off.
 *
 * The flag does not decide *whether* to localize. It decides *whose* conventions to localize by:
 * on, the reader's own; off, en-US. Both sides run the same formatters over the same resolver
 * output, so there is no second implementation to keep honest — only a second argument.
 *
 * That makes the flag-off screen the one this product shipped for years, rather than the raw wire.
 * A distance arriving in kilometres reads as miles either way; what a Mexican reader loses while
 * the flag is off is their own conventions, not the conversion itself. An en-US reader loses
 * nothing at all, which is worth stating as strongly as it sounds: for them the two sides are the
 * same string, member for member, and a test in `passthrough.test.ts` holds it there.
 *
 * The reader's **time zone is the one thing carried across**, and it is carried because it is not a
 * convention. Their locale says how they write a date; their zone says which date it is where they
 * are. Substituting UTC would move every timestamp on the page for anyone outside it — including
 * the en-US readers this is supposed to leave untouched.
 *
 * The cost of all this is that turning the flag off is not self-announcing: an environment left
 * switched off looks entirely plausible to a reviewer working in en-US, because for them it *is*
 * plausible. `enabled` is a required prop for that reason — the answer has to be stated at the root
 * rather than defaulted into.
 */

/**
 * How many zones are held at once.
 *
 * One session has one reader and so one zone, and the entries are small. The bound exists because
 * this package is a federation singleton that lives as long as the tab does, and a test or a
 * fixture sweeping zones should degrade to the uncached cost rather than grow the heap.
 */
const ZONE_CACHE_LIMIT = 20;

const byZone =
  <T>(store: Map<string, T>) =>
  (timeZone: string, build: () => T): T => {
    const hit = store.get(timeZone);
    if (hit !== undefined) return hit;

    const built = build();

    // Cleared wholesale rather than evicted one at a time: past the bound the working set has
    // already been left behind, and either entry costs little enough to rebuild that tracking
    // recency would not pay for itself.
    if (store.size >= ZONE_CACHE_LIMIT) store.clear();

    store.set(timeZone, built);
    return built;
  };

const inertPreferences = byZone(new Map<string, ResolvedPreferences>());
const inertBundles = byZone(new Map<string, FormatBundle>());
const inertCalendars = byZone(new Map<string, CalendarBundle>());
const inertPhones = byZone(new Map<string, PhoneBundle>());

/**
 * What the reader's preferences become while the flag is off: en-US, in their own zone.
 *
 * Built by running the real resolver over an en-US payload rather than by editing the reader's
 * resolved set field by field. The resolver is what knows that en-US implies US customary units,
 * month-first dates, a twelve-hour clock and dollars, and asking it here means the flag-off set
 * cannot drift from the flag-on set for a US reader — which is the whole guarantee.
 *
 * It also mints a correct `signature`, which {@link toServerParams} requires and which a spread of
 * some shared constant would get wrong: two readers in different zones must not share a memo key.
 *
 * @param reader - The reader's own resolved preferences, for their time zone.
 * @returns The en-US preference set to render and to speak to the server by.
 */
export const inertPreferencesFor = (
  reader: ResolvedPreferences
): ResolvedPreferences =>
  inertPreferences(
    reader.timeZone,
    () =>
      resolvePreferences({
        userPreferences: {
          locale: REGIONAL_DEFAULTS.locale,
          timeZone: reader.timeZone,
        },
      }).preferences
  );

/**
 * Assembles the flag-off bundle.
 *
 * Cached per zone rather than per call, because the bundle is promised to be safe in a dependency
 * array and a fresh set of closures each render would break every memo boundary below it.
 *
 * @param reader - The reader's own resolved preferences.
 * @returns A bundle of the same type as {@link buildFormatBundle}, rendering as en-US.
 */
export const buildPassThroughBundle = (
  reader: ResolvedPreferences
): FormatBundle =>
  inertBundles(reader.timeZone, () =>
    buildFormatBundle(inertPreferencesFor(reader))
  );

/**
 * Assembles the flag-off calendar, cached for the same reason the bundle above is.
 *
 * Only one member moves with the flag. The zone is the reader's on both sides, so every boundary
 * this computes already falls where they are; what en-US decides is where the week starts, which
 * is the same thing the flag decides about every other convention on the page.
 *
 * @param reader - The reader's own resolved preferences.
 * @returns A bundle of the same type as {@link buildCalendarBundle}, on an en-US week.
 */
export const buildPassThroughCalendar = (
  reader: ResolvedPreferences
): CalendarBundle =>
  inertCalendars(reader.timeZone, () =>
    buildCalendarBundle(inertPreferencesFor(reader))
  );

/**
 * Assembles the flag-off phone bundle, cached for the same reason the two above are.
 *
 * While the flag is off every reader's own country is the US, which is what this product assumed
 * before any of this existed.
 *
 * @param reader - The reader's own resolved preferences.
 * @returns A bundle of the same type as {@link buildPhoneBundle}, laying numbers out as en-US.
 */
export const buildPassThroughPhone = (
  reader: ResolvedPreferences
): PhoneBundle =>
  inertPhones(reader.timeZone, () =>
    buildPhoneBundle(inertPreferencesFor(reader))
  );
